import {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ReactNode,
} from "react";
import "./Button.css";

type ButtonVariant = "primary" | "ghost" | "danger" | "nav" | "pill";

type CommonButtonProps = {
  children: ReactNode;
  className?: string;
  variant?: ButtonVariant;
};

type AnchorButtonProps = CommonButtonProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "children" | "className"> & {
    href: string;
  };

type NativeButtonProps = CommonButtonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children" | "className"> & {
    href?: never;
  };

type ButtonProps = AnchorButtonProps | NativeButtonProps;

const variantClassMap: Record<ButtonVariant, string> = {
  primary: "primary-button",
  ghost: "ghost-button",
  danger: "danger-button",
  nav: "nav-button",
  pill: "pill-button",
};

function joinClassNames(...classes: Array<string | undefined>) {
  return classes.filter(Boolean).join(" ");
}

export default function Button({
  children,
  className,
  variant = "primary",
  ...props
}: ButtonProps) {
  const classes = joinClassNames(variantClassMap[variant], className);

  if ("href" in props) {
    const anchorProps = props as Omit<AnchorButtonProps, keyof CommonButtonProps>;
    return (
      <a className={classes} {...anchorProps}>
        {children}
      </a>
    );
  }

  const buttonProps = props as Omit<NativeButtonProps, keyof CommonButtonProps>;
  return (
    <button className={classes} {...buttonProps}>
      {children}
    </button>
  );
}
